import React, { useCallback, useEffect, useState } from 'react';

import { Link, useNavigate } from 'react-router-dom';

import Layout from '../../components/Layout/Layout';

import Loading from '../../components/Loading/Loading';

import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';

import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';

import SeoHead from '../../components/Seo/SeoHead';

import { useAuth } from '../../contexts/AuthContext';

import userService from '../../services/userService';

import universeService from '../../services/universeService';

import messagingService from '../../services/messagingService';

import { getUserProfilePath } from '../../utils/userPaths';

import {
  getStaffAvatarClass,
  getStaffFrameClass,
  getStaffRoleBadgeClass,
  getStaffRoleBadgeLabel,
  staffStyles,
} from '../../utils/userStaffRole';

import styles from './Players.module.css';



const PAGE_SIZE = 24;



const formatDate = (value) => {

  if (!value) return null;

  return new Date(value).toLocaleDateString('fr-FR', {

    day: 'numeric',

    month: 'long',

    year: 'numeric',

  });

};



const MessageIcon = ({ className }) => (

  <svg

    className={className}

    viewBox="0 0 24 24"

    fill="none"

    stroke="currentColor"

    strokeWidth="2"

    strokeLinecap="round"

    strokeLinejoin="round"

    aria-hidden="true"

  >

    <path d="M4 4h16v12H5.2L4 17.2V4z" />

    <path d="m4 4 8 6 8-6" />

  </svg>

);



const NewMemberIcon = () => (

  <span

    className={styles.newMemberBadge}

    title="Nouveau membre (moins d'un mois)"

    aria-label="Nouveau membre (moins d'un mois)"

  >

    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={styles.newMemberIcon}>

      <path d="M12 2l1.6 4.9H19l-4 2.9 1.5 4.9L12 14.8 7.6 14.7 9.1 9.8 5 6.9h5.4L12 2z" />

    </svg>

  </span>

);



const Players = () => {

  const navigate = useNavigate();

  const { user: currentUser } = useAuth();

  const [universes, setUniverses] = useState([]);

  const [players, setPlayers] = useState([]);

  const [pagination, setPagination] = useState(null);

  const [page, setPage] = useState(1);

  const [searchInput, setSearchInput] = useState('');

  const [searchQuery, setSearchQuery] = useState('');

  const [universeSlug, setUniverseSlug] = useState('');

  const [presence, setPresence] = useState('with');

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [messagingUserId, setMessagingUserId] = useState(null);

  const [messagingError, setMessagingError] = useState('');



  useEffect(() => {

    universeService.getUniverses()

      .then((data) => setUniverses(Array.isArray(data) ? data : []))

      .catch(() => setUniverses([]));

  }, []);



  const loadPlayers = useCallback(async (targetPage) => {

    try {

      setLoading(true);

      setError(null);

      const data = await userService.getPlayersList(

        targetPage,

        PAGE_SIZE,

        searchQuery,

        universeSlug,

        presence,

      );

      setPlayers(data.items || []);

      setPagination(data.pagination || null);

      setPage(targetPage);

    } catch (err) {

      setError(err.response?.data?.error || 'Erreur lors du chargement des joueurs');

      setPlayers([]);

      setPagination(null);

    } finally {

      setLoading(false);

    }

  }, [searchQuery, universeSlug, presence]);



  useEffect(() => {

    void loadPlayers(1);

  }, [loadPlayers]);



  const handleSearchSubmit = (event) => {

    event.preventDefault();

    setSearchQuery(searchInput.trim());

  };



  const handleUniverseChange = (event) => {

    setUniverseSlug(event.target.value);

    setPresence('with');

  };



  const handleSendMessage = async (player) => {

    if (!player?.id || messagingUserId) return;



    setMessagingUserId(player.id);

    setMessagingError('');



    try {

      const data = await messagingService.createConversation({ targetUserId: player.id });

      const conversationId = data?.conversation?.id;

      if (conversationId) {

        navigate(`/messagerie/${conversationId}`);

      } else {

        setMessagingError('Impossible d’ouvrir la conversation.');

      }

    } catch (err) {

      setMessagingError(err.response?.data?.error || 'Impossible d’ouvrir la conversation.');

    } finally {

      setMessagingUserId(null);

    }

  };



  const canMessagePlayer = (playerId) => (

    currentUser && currentUser.id !== playerId

  );



  const selectedUniverse = universes.find((universe) => universe.slug === universeSlug);

  const hasFilters = Boolean(searchQuery || universeSlug);



  const emptyMessage = () => {

    if (searchQuery && universeSlug) {

      return 'Aucun joueur ne correspond à ces critères.';

    }

    if (searchQuery) {

      return 'Aucun joueur ne correspond à votre recherche.';

    }

    if (universeSlug && presence === 'without') {

      return `Aucun joueur sans personnage validé dans ${selectedUniverse?.name || 'cet univers'}.`;

    }

    if (universeSlug) {

      return `Aucun joueur avec personnage validé dans ${selectedUniverse?.name || 'cet univers'}.`;

    }

    return 'Aucun joueur à afficher.';

  };



  const seo = {

    metaTitle: 'Joueurs — Earth Forum',

    metaDescription: 'Liste des joueurs inscrits sur Earth Forum.',

    robotsIndex: true,

  };



  if (loading && players.length === 0) {

    return (

      <Layout wide>

        <Loading message="Chargement des joueurs..." />

      </Layout>

    );

  }



  if (error && players.length === 0) {

    return (

      <Layout wide>

        <ErrorMessage message={error} onRetry={() => loadPlayers(1)} />

      </Layout>

    );

  }



  return (

    <Layout wide>

      <SeoHead seo={seo} />

      <div className={styles.content}>

        <Breadcrumb

          items={[

            { name: 'Accueil', url: '/', icon: 'home' },

            { name: 'Joueurs', url: null },

          ]}

        />



        <header className={styles.header}>

          <div>

            <h1 className={styles.title}>Joueurs</h1>

            <p className={styles.subtitle}>

              {pagination?.total ?? players.length} joueur{(pagination?.total ?? players.length) !== 1 ? 's' : ''}

              {universeSlug && selectedUniverse ? (

                <> — {presence === 'without' ? 'sans' : 'avec'} personnage · {selectedUniverse.name}</>

              ) : (

                <> inscrit{(pagination?.total ?? players.length) !== 1 ? 's' : ''}</>

              )}

            </p>

          </div>

          <form className={styles.searchForm} onSubmit={handleSearchSubmit}>

            <input

              type="search"

              className={styles.searchInput}

              value={searchInput}

              onChange={(event) => setSearchInput(event.target.value)}

              placeholder="Rechercher un pseudo…"

              aria-label="Rechercher un joueur"

            />

            <button type="submit" className={styles.searchButton}>

              Rechercher

            </button>

          </form>

        </header>



        <div className={styles.filters}>

          <label className={styles.filterField}>

            <span className={styles.filterLabel}>Univers</span>

            <select

              className={styles.filterSelect}

              value={universeSlug}

              onChange={handleUniverseChange}

              aria-label="Filtrer par univers"

            >

              <option value="">Tous les univers</option>

              {universes.map((universe) => (

                <option key={universe.id || universe.slug} value={universe.slug}>

                  {universe.name}

                </option>

              ))}

            </select>

          </label>



          {universeSlug && (

            <div className={styles.presenceTabs} role="tablist" aria-label="Filtrer par présence dans l'univers">

              <button

                type="button"

                role="tab"

                aria-selected={presence === 'with'}

                className={`${styles.presenceTab} ${presence === 'with' ? styles.presenceTabActive : ''}`}

                onClick={() => setPresence('with')}

              >

                Avec personnage

              </button>

              <button

                type="button"

                role="tab"

                aria-selected={presence === 'without'}

                className={`${styles.presenceTab} ${presence === 'without' ? styles.presenceTabActive : ''}`}

                onClick={() => setPresence('without')}

              >

                Sans personnage

              </button>

            </div>

          )}

        </div>



        {players.length > 0 ? (

          <>

            <div className={styles.playerGrid}>

              {players.map((player) => {
                const staffRoleBadgeLabel = getStaffRoleBadgeLabel(player.staffRole);

                return (

                <article key={player.id} className={`${styles.playerCard} ${getStaffFrameClass(player.staffRole)}`}>

                  {canMessagePlayer(player.id) ? (

                    <button

                      type="button"

                      className={styles.messageButton}

                      onClick={() => handleSendMessage(player)}

                      disabled={messagingUserId === player.id}

                      aria-label={`Envoyer un message à ${player.pseudo}`}

                      title={`Envoyer un message à ${player.pseudo}`}

                    >

                      <MessageIcon className={styles.messageIcon} />

                    </button>

                  ) : !currentUser ? (

                    <Link

                      to="/login"

                      className={styles.messageLink}

                      aria-label="Connectez-vous pour envoyer un message"

                      title="Connectez-vous pour envoyer un message"

                    >

                      <MessageIcon className={styles.messageIcon} />

                    </Link>

                  ) : null}

                  <Link

                    to={getUserProfilePath(player.id)}

                    className={styles.playerCardMain}

                  >

                    <div className={styles.playerCardHeader}>

                      {player.avatar ? (

                        <img src={player.avatar} alt={player.pseudo} className={`${styles.playerAvatar} ${getStaffAvatarClass(player.staffRole)}`} />

                      ) : (

                        <span className={`${styles.playerAvatarFallback} ${getStaffAvatarClass(player.staffRole)}`}>

                          {(player.pseudo || '?').charAt(0).toUpperCase()}

                        </span>

                      )}

                      <div className={styles.playerNameRow}>
                        <h2 className={styles.playerName}>{player.pseudo}</h2>
                        {player.isNewMember && <NewMemberIcon />}
                      </div>

                    </div>

                    {staffRoleBadgeLabel && (
                      <p className={staffStyles.staffRoleLine}>
                        <span className={`${staffStyles.staffRoleBadge} ${getStaffRoleBadgeClass(player.staffRole)}`}>
                          {staffRoleBadgeLabel}
                        </span>
                      </p>
                    )}

                    {player.createdAt && (

                      <p className={styles.playerMeta}>Membre depuis {formatDate(player.createdAt)}</p>

                    )}

                  </Link>

                </article>

              );
              })}

            </div>



            {messagingError && (

              <p className={styles.messagingError} role="alert">{messagingError}</p>

            )}



            {pagination && pagination.totalPages > 1 && (

              <nav className={styles.pagination} aria-label="Pagination des joueurs">

                <button

                  type="button"

                  className={styles.paginationButton}

                  disabled={page <= 1 || loading}

                  onClick={() => loadPlayers(page - 1)}

                >

                  Précédent

                </button>

                <span className={styles.paginationMeta}>

                  Page {page} sur {pagination.totalPages}

                </span>

                <button

                  type="button"

                  className={styles.paginationButton}

                  disabled={!pagination.hasNext || loading}

                  onClick={() => loadPlayers(page + 1)}

                >

                  Suivant

                </button>

              </nav>

            )}

          </>

        ) : (

          <p className={styles.emptyState}>

            {hasFilters ? emptyMessage() : 'Aucun joueur à afficher.'}

          </p>

        )}

      </div>

    </Layout>

  );

};



export default Players;


